import { Close } from '@highmobility/console-ui-components';
import { FC, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchVehicleClearanceHistory } from '@/services/apiService';
import { getClearanceNotes } from '@/utils/clearanceErrorMapper';
import { ROUTE_PATHS } from '@/router/routes';
import { StatusBadge } from '@/components/atoms/StatusBadge';
import { useTypedParams } from '@/hooks/useTypedParams';

export interface VehicleClearanceStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  details: {
    id: string;
    vin: string;
    currentStatus: string;
    oemName: string;
  } | null;
}

const formatStatus = (dbStatus: string) => {
  if (dbStatus === 'created') return 'Pending';
  return dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1);
};

export const VehicleClearanceStatusModal: FC<VehicleClearanceStatusModalProps> = ({
  isOpen,
  onClose,
  details,
}) => {
  const { teamId, projectId } = useTypedParams<typeof ROUTE_PATHS.VEHICLE_CLEARANCE>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicle-clearance-history', teamId, projectId, details?.id],
    queryFn: () => fetchVehicleClearanceHistory(teamId, projectId, details!.id),
    enabled: isOpen && !!details?.id,
  });

  const historyEvents = Array.isArray(data?.data?.data) ? data.data.data : [];

  const sortedHistory = [...historyEvents].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !details) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-opacity">
      <div
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-black-20 bg-[#1F2F32] shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between rounded-t-lg border-b border-black-20 bg-[#2B4247] px-6 py-4">
          <div className="flex items-center gap-4">
            <h3 className="text-base font-medium text-white">Clearance History</h3>
            <span className="rounded bg-black/20 px-2 py-1 font-mono text-sm text-white-60">
              {details.vin}
            </span>
            <div className="h-4 w-px bg-white-20"></div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white-60">Current Status:</span>
              <StatusBadge status={details.currentStatus as any} />
            </div>
          </div>

          <button
            onClick={onClose}
            className="ml-4 shrink-0 text-white-40 transition-colors hover:text-white"
            aria-label="Close modal"
          >
            <Close className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="bg-dark-green overflow-hidden rounded-lg border border-black-20">
            <table className="w-full text-left text-sm text-white">
              <thead className="border-b border-black-20 bg-[#2B4247]">
                <tr>
                  <th className="w-32 px-4 py-3 font-medium text-white-80">Status</th>
                  <th className="w-48 px-4 py-3 font-medium text-white-80">Timestamp</th>
                  <th className="px-4 py-3 font-medium text-white-80">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black-20">
                {isLoading && (
                  <tr>
                    <td colSpan={3} className="p-8 text-center text-sm text-white-40">
                      Loading history...
                    </td>
                  </tr>
                )}

                {!isLoading && error && (
                  <tr>
                    <td colSpan={3} className="p-8 text-center text-sm text-red-500">
                      Failed to load history.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  !error &&
                  sortedHistory.map((row: any, index: number) => {
                    const isLatest = index === 0;
                    const displayNotes = getClearanceNotes(details.oemName, row.notes);
                    return (
                      <tr
                        key={`${row.status}-${row.timestamp}`}
                        className={`transition-colors hover:bg-white/5 ${
                          isLatest ? 'bg-white/[0.02]' : ''
                        }`}
                      >
                        <td className="px-4 py-4 align-top">
                          <StatusBadge status={formatStatus(row.status) as any} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 align-top text-white-60">
                          {row.timestamp}
                        </td>
                        <td className="px-4 py-4 align-top leading-relaxed text-white-80">
                          {displayNotes || (
                            <span className="italic text-white-40">No notes provided.</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {!isLoading && !error && sortedHistory.length === 0 && (
              <div className="p-8 text-center text-sm text-white-40">
                No history events found for this vehicle.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
